/**
 * The Spotlight on Vera Home: an admin-scheduled card with a title, a short
 * body, and optionally an image, a video and a multi-choice poll (Sasha 10/02,
 * first used for naming the balloon dog). Overlapping live ones stack, one
 * card each, newest start first (Sasha 10/12).
 *
 * The poll: one vote per person, changeable until voting closes. Results are
 * hidden until close unless the admin chose "after each vote"; the server only
 * sends numbers the caller may see, so there is nothing to peek at. A write-in
 * (when switched on) is the voter's own answer, never added to the list other
 * people see; identical write-ins count as one option in the results.
 *
 * Plan: docs/design/home/PLAN.md, "Spotlight".
 */

import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { Button, Input, makeStyles, mergeClasses, tokens } from '@fluentui/react-components';
import { EyeRegular, SparkleFilled, TrophyFilled } from '@fluentui/react-icons';
import { borderRadius, semanticColors, veracityColors } from '@shared/ui';
import { videoEmbedUrl } from '@widgets/academy-content/videoEmbed';
import {
  spotlightImageUrl,
  spotlightVideoUrl,
  useSpotlightVote,
  type Spotlight,
  type SpotlightPoll,
} from './api';

const useStyles = makeStyles({
  card: {
    backgroundColor: '#FFFFFF',
    borderRadius: borderRadius.editorial,
    border: `1px solid ${semanticColors.borderSubtle}`,
    borderTop: `4px solid ${veracityColors.daybreakAmber}`,
    padding: '14px 16px 16px',
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    minWidth: 0,
  },
  head: { display: 'flex', alignItems: 'center', gap: '8px' },
  label: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '10px',
    fontWeight: 700,
    letterSpacing: '0.16em',
    textTransform: 'uppercase',
    color: tokens.colorBrandForegroundLink,
  },
  manage: {
    marginLeft: 'auto',
    fontSize: '12px',
    fontWeight: 600,
    color: tokens.colorBrandForegroundLink,
    textDecoration: 'none',
    ':hover': { textDecoration: 'underline' },
  },
  title: {
    margin: 0,
    fontSize: '19px',
    fontWeight: 700,
    letterSpacing: '-0.02em',
    lineHeight: 1.2,
    color: veracityColors.midnightCharcoal,
  },
  image: {
    width: '100%',
    maxHeight: '240px',
    objectFit: 'contain',
    borderRadius: borderRadius.large,
    backgroundColor: veracityColors.firstLight,
  },
  video: {
    width: '100%',
    aspectRatio: '16 / 9',
    border: 'none',
    borderRadius: borderRadius.large,
    backgroundColor: veracityColors.midnightCharcoal,
  },
  body: {
    margin: 0,
    fontSize: '13.5px',
    lineHeight: 1.55,
    color: '#3A3431',
    whiteSpace: 'pre-wrap',
    overflowWrap: 'anywhere',
  },

  poll: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    paddingTop: '10px',
    borderTop: `1px solid ${semanticColors.borderSubtle}`,
  },
  question: { fontSize: '14px', fontWeight: 600, color: veracityColors.midnightCharcoal, lineHeight: 1.35 },
  options: { display: 'flex', flexDirection: 'column', gap: '6px' },
  option: {
    position: 'relative',
    overflow: 'hidden',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 14px',
    borderRadius: borderRadius.circular,
    border: `1px solid ${semanticColors.borderDefault}`,
    backgroundColor: '#FFFFFF',
    color: veracityColors.midnightCharcoal,
    fontFamily: 'inherit',
    fontSize: '13px',
    fontWeight: 600,
    textAlign: 'left',
    cursor: 'pointer',
    ':hover': { backgroundColor: veracityColors.firstLight },
    ':disabled': { cursor: 'default' },
    ':focus-visible': { outline: `2px solid ${tokens.colorBrandStroke1}`, outlineOffset: '2px' },
  },
  chosen: {
    backgroundColor: tokens.colorBrandBackground,
    border: `1px solid ${tokens.colorBrandBackground}`,
    color: '#FFFFFF',
    ':hover': { backgroundColor: tokens.colorBrandBackgroundHover },
  },
  writeIn: { display: 'flex', gap: '6px' },

  // Results: one bar per answer, the share as a fill behind the label.
  row: {
    position: 'relative',
    overflow: 'hidden',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '7px 12px',
    borderRadius: borderRadius.medium,
    backgroundColor: veracityColors.firstLight,
    fontSize: '13px',
    color: veracityColors.midnightCharcoal,
  },
  fill: {
    position: 'absolute',
    inset: '0 auto 0 0',
    backgroundColor: veracityColors.gentleHaze,
    transitionProperty: 'width',
    transitionDuration: '400ms',
    '@media (prefers-reduced-motion: reduce)': { transitionDuration: '0ms' },
  },
  fillWinner: { backgroundColor: veracityColors.daybreakAmber },
  rowLabel: { position: 'relative', flex: 1, minWidth: 0, fontWeight: 600, overflowWrap: 'anywhere' },
  rowMine: { position: 'relative', fontSize: '11px', fontWeight: 600, color: semanticColors.textSecondary },
  rowShare: { position: 'relative', fontVariantNumeric: 'tabular-nums', fontWeight: 700 },
  winner: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '14px',
    fontWeight: 700,
    color: veracityColors.midnightCharcoal,
  },
  trophy: { color: veracityColors.veracityOrange, fontSize: '20px' },
  meta: { fontSize: '11.5px', color: semanticColors.textSecondary, lineHeight: 1.4 },
  previewBar: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    flexWrap: 'wrap',
    padding: '8px 10px',
    borderRadius: borderRadius.medium,
    backgroundColor: veracityColors.firstLight,
    border: `1px dashed ${veracityColors.daybreakAmber}`,
    fontSize: '12.5px',
    fontWeight: 600,
    color: veracityColors.midnightCharcoal,
  },
  previewLinks: { marginLeft: 'auto', display: 'flex', gap: '12px' },
  previewLink: { color: tokens.colorBrandForegroundLink, fontWeight: 600, textDecoration: 'none', ':hover': { textDecoration: 'underline' } },
  error: { fontSize: '12px', color: tokens.colorPaletteRedForeground1 },
});

function pct(n: number, total: number): number {
  return total === 0 ? 0 : Math.round((n / total) * 100);
}

function dayLabel(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}

function SpotlightPollView({ id, poll, preview = false }: { id: string; poll: SpotlightPoll; preview?: boolean }) {
  const styles = useStyles();
  const vote = useSpotlightVote();
  // The vote response carries the fresh spotlight; use it until the summary refetch lands.
  const fresh = vote.variables?.id === id ? vote.data?.spotlight.poll ?? null : null;
  const p = fresh ?? poll;
  const myOption = p.myVote && 'optionId' in p.myVote ? p.myVote.optionId : null;
  const myWriteIn = p.myVote && 'writeIn' in p.myVote ? p.myVote.writeIn : null;
  const [draft, setDraft] = useState('');
  // Results showing before close (after_vote mode, or an admin): the options
  // come back on request, to change a pick.
  const [changing, setChanging] = useState(false);

  const submitWriteIn = (e: FormEvent) => {
    e.preventDefault();
    const writeIn = draft.trim();
    if (!writeIn || vote.isPending || preview) return;
    vote.mutate({ id, writeIn }, { onSuccess: () => { setDraft(''); setChanging(false); } });
  };

  const results = p.results;
  const top = results ? Math.max(0, ...results.rows.map((r) => r.votes)) : 0;
  const winners = results && p.closed && top > 0 ? results.rows.filter((r) => r.votes === top) : [];
  const mineKey = myOption ?? null;
  const showOptions = !p.closed && (!results || changing);

  return (
    <div className={styles.poll}>
      <span className={styles.question}>{p.question}</span>

      {p.closed && winners.length > 0 && (
        <span className={styles.winner}>
          <TrophyFilled className={styles.trophy} aria-hidden />
          {winners.length === 1 ? `And the winner is: ${winners[0].label}` : `A tie: ${winners.map((w) => w.label).join(' and ')}`}
        </span>
      )}

      {results && !changing ? (
        <div className={styles.options} role="list" aria-label="Results">
          {results.rows.map((r) => {
            const share = pct(r.votes, results.total);
            const isWinner = p.closed && r.votes === top && top > 0;
            const mine = r.key === mineKey || (r.writeIn && myWriteIn !== null && r.label.toLowerCase() === myWriteIn.toLowerCase());
            return (
              <div key={r.key} role="listitem" className={styles.row} aria-label={`${r.label}: ${share}%, ${r.votes} votes`}>
                <span className={mergeClasses(styles.fill, isWinner && styles.fillWinner)} style={{ width: `${share}%` }} />
                <span className={styles.rowLabel}>{r.label}</span>
                {mine && <span className={styles.rowMine}>your pick</span>}
                <span className={styles.rowShare}>{share}%</span>
              </div>
            );
          })}
        </div>
      ) : null}

      {showOptions && (
        <>
          <div className={styles.options} role="radiogroup" aria-label={p.question}>
            {p.options.map((o) => (
              <button
                key={o.id}
                type="button"
                role="radio"
                aria-checked={myOption === o.id}
                className={mergeClasses(styles.option, myOption === o.id && styles.chosen)}
                disabled={vote.isPending || preview}
                onClick={() => myOption !== o.id && vote.mutate({ id, optionId: o.id }, { onSuccess: () => setChanging(false) })}
              >
                {o.label}
              </button>
            ))}
          </div>
          {p.allowWriteIn && (
            <form className={styles.writeIn} onSubmit={submitWriteIn}>
              <Input
                size="small"
                style={{ flex: 1, minWidth: 0 }}
                placeholder={myWriteIn ? `Your pick: ${myWriteIn}` : 'Or write in your own'}
                value={draft}
                maxLength={60}
                onChange={(_, d) => setDraft(d.value)}
                aria-label="Write in your own answer"
              />
              <Button size="small" type="submit" disabled={!draft.trim() || vote.isPending || preview}>
                Vote
              </Button>
            </form>
          )}
        </>
      )}
      {p.closed && !results && (
        <span className={styles.meta}>Voting has closed. The results are coming soon.</span>
      )}

      {!p.closed && results && !changing && (
        <Button
          size="small"
          appearance="subtle"
          style={{ alignSelf: 'flex-start' }}
          disabled={preview}
          onClick={() => setChanging(true)}
        >
          {p.myVote ? 'Change my pick' : 'Vote'}
        </Button>
      )}

      <span className={styles.meta}>
        {p.closed
          ? `Voting closed ${dayLabel(p.closesAt)} · ${p.voteCount} vote${p.voteCount === 1 ? '' : 's'}`
          : [
              `${p.voteCount} vote${p.voteCount === 1 ? '' : 's'} so far`,
              p.myVote
                ? myWriteIn
                  ? `you wrote in ${myWriteIn}, change it any time`
                  : 'you can change your pick'
                : null,
              results
                ? `closes ${dayLabel(p.closesAt)}`
                : p.resultsMode === 'on_share'
                  ? `voting closes ${dayLabel(p.closesAt)}, results shared after`
                  : `results revealed when voting closes ${dayLabel(p.closesAt)}`,
            ]
              .filter(Boolean)
              .join(' · ')}
      </span>
      {vote.error && <span className={styles.error}>{vote.error.message}</span>}
    </div>
  );
}

export function SpotlightCard({
  spotlight: s,
  canManage,
  preview = false,
}: {
  spotlight: Spotlight;
  canManage: boolean;
  /** A Spotlight Admin's preview (/?previewSpotlight=<id>): a banner, and voting switched off. */
  preview?: boolean;
}) {
  const styles = useStyles();
  const embed = s.videoUrl ? videoEmbedUrl(s.videoUrl) : null;
  return (
    <section className={styles.card} aria-labelledby={`spotlight-${s.id}`}>
      <div className={styles.head}>
        <span className={styles.label}>
          <SparkleFilled style={{ fontSize: 14, color: veracityColors.veracityOrange }} aria-hidden />
          Spotlight
        </span>
        {canManage && (
          <Link to="/central/spotlight" className={styles.manage}>
            Manage
          </Link>
        )}
      </div>
      {preview && (
        <div className={styles.previewBar} role="status">
          <EyeRegular aria-hidden />
          Preview: only you can see this. Voting is off.
          <span className={styles.previewLinks}>
            <Link to="/central/spotlight" className={styles.previewLink}>
              Back to Spotlight admin
            </Link>
            <Link to="/" className={styles.previewLink}>
              Exit preview
            </Link>
          </span>
        </div>
      )}
      <h2 id={`spotlight-${s.id}`} className={styles.title}>
        {s.title}
      </h2>
      {s.imageItemId && <img className={styles.image} src={spotlightImageUrl(s.imageItemId)} alt={s.imageAlt} />}
      {s.videoItemId && (
        <video className={styles.video} src={spotlightVideoUrl(s.videoItemId)} title={s.title} controls preload="metadata" playsInline />
      )}
      {embed && <iframe className={styles.video} src={embed} title={s.title} allowFullScreen allow="encrypted-media" />}
      {s.body && <p className={styles.body}>{s.body}</p>}
      {s.poll && <SpotlightPollView id={s.id} poll={s.poll} preview={preview} />}
    </section>
  );
}
